import React, { useEffect, useState } from 'react';
import '../../Styles/tools/QADataset.css';
import { getToken } from '../../Utils/Common';
import { getKategoriesDataAPI } from '../../API/kategories';
import { Multiselect } from 'multiselect-react-dropdown';
import Loader from 'react-loader-advanced';
import Oval from 'react-loader-spinner';
import ManageQAPairsModal from './ManageQAPairsComponent';
import { CheckSquare } from 'react-feather';
import { ButtonGroup, Button } from 'react-bootstrap';
import Select from 'react-select';

const QADatasetBuilder = () => {
  const [sections, setSections] = useState([]);
  const [qaDatasetStatuses, setQADatasetStatuses] = useState(new Set([]));
  const [questionsByCategory, setQuestionsByCategory] = useState({});
  const [question, setQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [qaPairs, setQaPairs] = useState({});
  const [currentParagraph, setCurrentParagraph] = useState(null);
  const [contextAnnotations, setContextAnnotations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const url = process.env.REACT_APP_BACKEND_API_URI;
  const base_url = process.env.REACT_APP_BASE_URI;

  useEffect(() => {
    fetchCategoriesAndQuestions();
  }, []);

  useEffect(() => {
    if (selectedCategories?.length === 0) {
      setSections([]);
      setQuestions([]);
      setCurrentParagraph(null);
      return;
    }
    Promise.all([fetchQADatasetStatuses(), fetchSectionsByCategories()]);
    const filteredQuestions = selectedCategories
                                              .map(key => questionsByCategory[key])
                                              .filter(value => value !== undefined).flat();
    setQuestions(filteredQuestions);
  }, [selectedCategories]);

  const fetchCategoriesAndQuestions = async () => {
    try {
      setCategoriesLoading(true)
      const [categoriesResponse, questionsByCategoryData] =
          await Promise.all([getKategoriesDataAPI(), fetchQuestionsByCategory()]);
      const categoryOptions = categoriesResponse.map(o => o.category).sort();
      setCategories(categoryOptions);
      setQuestionsByCategory(questionsByCategoryData);
      setCategoriesLoading(false)
    } catch (error) {
      console.error("Error fetching categories or questions:", error);
    }
  };

  const fetchQuestionsByCategory = async () => {
    try {
      const questionsByCatResponse = await fetch(`${base_url}/get_questions_by_category`, {
        method: 'GET'
      });

      return await questionsByCatResponse.json();

    } catch (error) {
      console.error("Error fetching questions by categories:", error);
    }
  };

  const fetchQADatasetStatuses = async () => {
    try {
      const qaDatasetStatusesResp = await fetch(`${base_url}/get_qa_dataset_statuses`, {
        method: 'GET'
      });
      const qaDatasetStatuses = await qaDatasetStatusesResp.json();
      setQADatasetStatuses(new Set(qaDatasetStatuses["completed"]));
    } catch (e) {
      console.log("Error fetching QA dataset statuses:", e);
    }
  }

  const updateQADatasetStatuses = async (row_id) => {
    try {
      await fetch(`${base_url}/update_qa_dataset_statuses`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "completed": row_id
        })
      });
    } catch (e) {
      console.log("Error updating QA dataset statuses:", e);
    }
  }

  const fetchSectionsByCategories = async () => {
    setIsLoading(true);

    try {
      const sectionsResponse = await fetch(`${url}parser/get_positions_by_category`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "token": getToken(),
          "positionCategory": selectedCategories
        })
      });

      const sectionsData = await sectionsResponse.json();
      console.log("Fetched sections data:", sectionsData);

      if (sectionsData && sectionsData.data) {
        setSections(sectionsData.data);
        setIsLoading(false);
      } else {
        console.error("Unexpected data structure:", sectionsData);
      }
    } catch (error) {
      console.error("Error fetching sections by categories:", error);
    }
  };

  const saveQAPairsToDataset = async () => {
    const rowId = currentParagraph.row_id;
    const short_text = currentParagraph.hinweis;
    const context = currentParagraph.long_text;

    try {
      const res = await fetch(`${base_url}/save_to_qa_dataset`, {
        //const res = await fetch('http://localhost:5001/save_to_qa_dataset', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "row_id": rowId,
          "short_text": short_text,
          "context": context,
          "qa_pairs": qaPairs[rowId]
        })
      });
      return await res.json();
    } catch (error) {
      console.error("Error fetching sections by categories:", error);
    }
  };


  const handleTextSelection = (paragraph) => {

    if (currentParagraph && paragraph.row_id !== currentParagraph.row_id) {
      if (qaPairs[currentParagraph.row_id] && qaPairs[currentParagraph.row_id].length > 0) {
        const switchParagraph = confirm("You have unsaved pending QA pairs in Paragraph " + (currentParagraph.number + 1) + ".\n" +
            "Are you sure you want to discard them and switch to another paragraph?");
        if (switchParagraph) {
          setContextAnnotations([]);
          setCurrentParagraph(null);
          setCompletedQuestions([])
          setQaPairs({});
        } else {
          return;
        }
      }
      setContextAnnotations([]);
    }

    setCurrentParagraph(paragraph);

    const selection = window.getSelection();
    if (selection.toString().length === 0) return;

    const startOffset = selection.anchorOffset;
    const endOffset = selection.focusOffset;

    const annotation = {
      id: Date.now(),
      start: Math.min(startOffset, endOffset),
      end: Math.max(startOffset, endOffset),
      text: selection.toString(),
      rowId: paragraph.row_id
    };

    const updatedAnnotations = [...contextAnnotations, annotation];
    setContextAnnotations(updatedAnnotations);

    window.getSelection().removeAllRanges();
  };

  const handleQuestionChange = (e) => {
    if (!e) return;
    setQuestion(e);
  };

  const saveQuestion = () => {

    if (contextAnnotations.length === 0) {
      const noAnswers = confirm("Really finalize question with no answers?");
      if (!noAnswers) {
        return;
      }
    }
    if (!currentParagraph) {
      return;
    }
    const rowId = currentParagraph.row_id;
    const paragraph = sections.find(sec => sec.row_id === rowId);
    if (!paragraph) return;

    let newQAPair;

    if (!qaPairs[rowId]) {
      qaPairs[rowId] = [];
      newQAPair = { question: question, answer: contextAnnotations };
    } else {
      const existingAnswers = qaPairs[rowId].flatMap(obj => obj.answer);
      const contextAnnotationsFiltered = contextAnnotations.filter(ann => !existingAnswers.some(ans => ans.id === ann.id));
      newQAPair = { question: question, answer: contextAnnotationsFiltered };
    }

    qaPairs[rowId].push(newQAPair);
    setQaPairs({ ...qaPairs });
    completedQuestions.push(question);
    setCompletedQuestions([...completedQuestions]);
    setContextAnnotations([]);
    setQuestion(null)
  }

  const showEditQAPairsModal = () => {
    setShowQAModal(true);
  }

  const storeQAPairs = async () => {
    const response = await saveQAPairsToDataset();
    updateQADatasetStatuses(currentParagraph.row_id);
    qaDatasetStatuses.add(currentParagraph.row_id);
    setQADatasetStatuses(new Set([...qaDatasetStatuses]));
    setCurrentParagraph(null);
    setContextAnnotations([]);
    setCompletedQuestions([]);
    setQaPairs({});
    setShowQAModal(false);
    alert(response);
  };

  const styles = {
    container: {
      position: 'relative'
    },
    icon: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      color: 'lightgreen'
    }
  };

  function handleDoubleClick(id) {

    const filteredAnnotations = contextAnnotations.filter((ann) => ann.id !== id)
    setContextAnnotations([...filteredAnnotations])

    qaPairs[currentParagraph.row_id] = qaPairs[currentParagraph.row_id]?.map(parentObj => {
        const filteredAnswers = parentObj.answer.filter(answer => answer.id !== id);
        return { ...parentObj, answer: filteredAnswers };
      }).filter(parent => parent.answer.length > 0);

    setQaPairs({...qaPairs});
  }

  return (
      <>
        <div className="qa-dataset-builder">
          <Loader show={categoriesLoading}
              message={<span>Loading, please wait...</span>}
          >
              <div className="multiselect-container">
              <Multiselect
                placeholder={'Select Position Categories'}
                closeOnSelect={true}
                isObject={false}
                options={categories}
                selectedValues={selectedCategories}
                onSelect={(selectedList) => setSelectedCategories(selectedList)}
                onRemove={(selectedList) => setSelectedCategories(selectedList)}
                avoidHighlightFirstOption={true}
                showCheckbox
              />
          </div>
          </Loader>
      <div className="content-wrapper">
        <div className="document-container">
          <Loader
              show={isLoading}
              message={<span>
              <Oval
                  visible={true}
                  height="60"
                  width="60"
                  color="#ffffff"
                  ariaLabel="oval-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                  type="Oval" /></span>}
          >
            <div className="document-viewer">
              {sections.length > 0 ? (
                  sections.map((paragraph, index) => {
                    const updatedParagraph = { ...paragraph, number: index };

                    return (
                        <div key={index} className="paragraph" style={styles.container}>
                          {qaDatasetStatuses.has(updatedParagraph.row_id) && (
                              <CheckSquare size="26px" style={styles.icon} />
                          )}
                          <h3>{`Paragraph ${index + 1}`}</h3>
                          <div className="paragraph-details-column">
                            <div><strong>Ref:</strong> {updatedParagraph.ref}</div>
                            <div><strong>Type:</strong> {updatedParagraph.type}</div>
                            <div><strong>Unit:</strong> {updatedParagraph.unit}</div>
                            <div><strong>Menge:</strong> {updatedParagraph.menge}</div>
                            <div><strong>Hinweis:</strong> {updatedParagraph.hinweis}</div>
                          </div>
                          <p
                              onMouseUp={() => handleTextSelection(updatedParagraph)}
                              dangerouslySetInnerHTML={{ __html: updatedParagraph.long_text }}
                              className="long-text"
                          />
                        </div>
                    );
                  })
              ) : (
                  <p>No sections available.</p>
              )}
            </div>
          </Loader>
          <div className="paragraph-count">
            <p><strong>Total Paragraphs:</strong> {sections.length}</p>
          </div>
        </div>
        <div className="qa-input-form">
          <h2>QA Input Form</h2>
          {currentParagraph && (
              <div>
                <h4>Current context: Paragraph {currentParagraph.number + 1}</h4>
                <br/>
                {(contextAnnotations && contextAnnotations.length > 0) && (
                    <table className="question-table">
                      <thead>
                      <tr><th>Selections  (double-click to remove)</th></tr>
                      </thead>
                      <tbody>
                        {contextAnnotations.map((annotation, index) => (
                          <tr key={index} onDoubleClick={() => handleDoubleClick(annotation.id)}>
                            <td>{annotation.text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                )}
              </div>
          )}
          <br/>
          <Select
              options={questions.map(q => ({ 'label': q.question, 'value': q.advice_id }))}
              value={question}
              placeholder="Select question"
              getOptionLabel={question => (
                  <span style={{ color: completedQuestions.includes(question?.label) ? 'green' : 'black' }}>
                {question.label} {completedQuestions.includes(question?.label) && '✓'}
              </span>
              )}
              onChange={(e) => handleQuestionChange(e)}
            isDisabled={!currentParagraph}
          />
          <br/>
          <ButtonGroup>
            <Button variant="secondary" size="sm" disabled={question === null || !currentParagraph} style={{marginRight: "8px"}}
                    onClick={() => { saveQuestion() }}>
              Finalize question
            </Button>
            <Button variant="secondary" size="sm"
                    disabled={!currentParagraph || !qaPairs[currentParagraph.row_id]}
                    style={{marginRight: "8px"}}
                    onClick={() => { showEditQAPairsModal() }}>
              View
            </Button>
            <Button variant="primary" size="sm"
                    disabled={currentParagraph == null ||
                              qaPairs[currentParagraph.row_id] == null ||
                              qaPairs[currentParagraph.row_id].length === 0}
                    onClick={() => { storeQAPairs() }}>
              Save to dataset
            </Button>
          </ButtonGroup>
        </div>
      </div>
        </div>
        <ManageQAPairsModal show={showQAModal}
                            qaPairs={qaPairs}
                            row_id={currentParagraph?.row_id}
                            onHide={() => setShowQAModal(false)}
                            contextAnnotations={contextAnnotations}
                            setContextAnnotations={setContextAnnotations}
                            completedQuestions={completedQuestions}
                            setCompletedQuestions={setCompletedQuestions}
                            storeQAPair={() => storeQAPairs()}/>
  </>
  );
};

export default QADatasetBuilder;